import type { Container, Sprite } from 'pixi.js';
import { clamp } from '../../shared/scalar';
import type { ClassDrawing, PaintedDrawing } from '../drawings';
import { type Shape, paintSheet } from '../paint';
import { type Look, fade, spring } from '../player-look';
import type { Textures } from '../textures';
import { REFERENCE_RADIUS } from '../textures-looks';
import { add, hide, placeOutline, setTint } from '../util';
import {
  BONNET,
  CREAM,
  JONGLEUR_PIECES,
  POMPOM,
  POMPOM_AT,
  type Slot,
  UNIT,
  clubColor,
  clubsOf,
  juggleSlot,
} from './jongleur-paint';

type Parts = Readonly<Record<keyof typeof JONGLEUR_PIECES, Shape>>;

const CLUBS = 3;
const EXTENT = 66;
const MIDDLE = 14;
const WHIP_TICKS = 1.6;
const BOB_TICKS = 2;
const RECOIL = 2.5;
const FLICK = 3.5;
const SPIN_STRETCH = { most: 0.55, ticks: 9, wobble: 0.42, lasts: 48 };
const BOBBLE = { stiffness: 0.16, damping: 0.22, lag: 0.55, most: 3.2, beat: 0.9, calmBeat: 0.3 };
const BOOST_GLOW = 24;

function tossStretch(sinceSkill: number): number {
  if (sinceSkill < 0 || sinceSkill >= SPIN_STRETCH.lasts) {
    return 1;
  }
  return (
    1 +
    SPIN_STRETCH.most *
      Math.exp(-sinceSkill / SPIN_STRETCH.ticks) *
      Math.cos(sinceSkill * SPIN_STRETCH.wobble)
  );
}

interface ClubSprites {
  readonly handle: Sprite;
  readonly body: Sprite;
  readonly glow: Sprite;
}

function createJugglerLook(
  bodies: Container,
  glows: Container,
  textures: Textures,
  parts: Parts,
): Look {
  const outline = add(bodies, parts.vest);
  const vest = add(bodies, parts.vest);
  const harlequin = add(bodies, parts.harlequin);
  const hands = add(bodies, parts.hands);
  const head = add(bodies, parts.head);
  const bonnet = add(bodies, parts.bonnet);
  const pompom = add(bodies, parts.pompom);
  const body = [outline, vest, harlequin, hands, head, bonnet, pompom];
  const clubs: ClubSprites[] = [];
  const springs = new Float64Array(4);
  const slot: Slot = { along: 0, across: 0, turn: 0 };
  let lastBeat = -1;
  let fresh = true;
  let originX = 0;
  let originY = 0;
  let cos = 1;
  let sin = 0;
  let size = 1;
  let facing = 0;

  function put(sprite: Sprite, along: number, across: number, turn: number): void {
    sprite.position.set(
      originX + (along * cos - across * sin) * UNIT * size,
      originY + (along * sin + across * cos) * UNIT * size,
    );
    sprite.rotation = facing + turn;
  }

  function club(index: number): ClubSprites {
    let found = clubs[index];
    if (found === undefined) {
      found = {
        glow: add(glows, textures.halo),
        handle: add(bodies, parts.clubHandle),
        body: add(bodies, parts.clubBody),
      };
      clubs[index] = found;
    }
    return found;
  }

  function hideClubs(from: number): void {
    for (let index = from; index < clubs.length; index += 1) {
      const sprites = clubs[index];
      if (sprites !== undefined) {
        hide(sprites.handle, sprites.body, sprites.glow);
      }
    }
  }

  return {
    reset() {
      springs.fill(0);
      lastBeat = -1;
      fresh = true;
    },
    hide() {
      hide(...body);
      hideClubs(0);
    },
    place(input) {
      const { player, x, y, angle, scale, color, frame, dt, state } = input;
      const { now, calm, palette, light, tempo } = frame;
      originX = x;
      originY = y;
      cos = Math.cos(angle);
      sin = Math.sin(angle);
      size = scale;
      facing = angle;

      const whip = input.fireCount > 0 ? fade(input.sinceFire, WHIP_TICKS) : 0;
      const bob = fade(input.sinceBeat, BOB_TICKS) * (calm ? 0.35 : 1);
      const recoil = -RECOIL * whip * (calm ? 0.5 : 1);
      if (input.beatIndex !== lastBeat) {
        if (!fresh) {
          springs[1] = (springs[1] ?? 0) - (calm ? BOBBLE.calmBeat : BOBBLE.beat);
        }
        lastBeat = input.beatIndex;
      }
      fresh = false;
      const along = ((player.x - player.prevX) * cos + (player.y - player.prevY) * sin) / scale;
      const across = (-(player.x - player.prevX) * sin + (player.y - player.prevY) * cos) / scale;
      spring(
        springs,
        0,
        clamp(-along * BOBBLE.lag, -BOBBLE.most, BOBBLE.most),
        BOBBLE.stiffness,
        BOBBLE.damping,
        dt,
      );
      spring(
        springs,
        2,
        clamp(-across * BOBBLE.lag, -BOBBLE.most, BOBBLE.most),
        BOBBLE.stiffness,
        BOBBLE.damping,
        dt,
      );

      for (const sprite of body) {
        sprite.visible = true;
      }
      const wide = scale * (1 + 0.05 * bob);
      const short = scale * (1 - 0.03 * bob);
      put(vest, recoil, 0, 0);
      vest.scale.set(short, wide);
      setTint(vest, CREAM);
      placeOutline(outline, vest, parts.vest.texture, REFERENCE_RADIUS, frame);
      put(harlequin, recoil, 0, 0);
      harlequin.scale.set(short, wide);
      setTint(harlequin, color);
      put(hands, recoil + FLICK * whip, 0, 0);
      hands.scale.set(scale);
      setTint(hands, input.teint);
      put(head, recoil, 0, 0);
      head.scale.set(scale);
      setTint(head, input.teint);
      put(bonnet, recoil, 0, 0);
      bonnet.scale.set(scale);
      setTint(bonnet, BONNET);
      put(pompom, recoil + POMPOM_AT + (springs[0] ?? 0), springs[2] ?? 0, 0);
      pompom.scale.set(scale);
      setTint(pompom, POMPOM);

      const owned = clubsOf(state, player);
      const count = owned.length === 0 ? CLUBS : owned.length;
      const beats = now / tempo.ticksPerBeat;
      const toss = tossStretch(input.sinceSkill);
      for (let index = 0; index < count; index += 1) {
        const prop = owned[index];
        const sprites = club(index);
        if (prop !== undefined && prop.phase !== 'held') {
          hide(sprites.handle, sprites.body, sprites.glow);
          continue;
        }
        juggleSlot(beats, index, count, slot);
        const turn = calm ? 0 : slot.turn;
        const reach = recoil + whip * FLICK + slot.along * toss;
        sprites.handle.visible = sprites.body.visible = true;
        put(sprites.handle, reach, slot.across, turn);
        put(sprites.body, reach, slot.across, turn);
        sprites.handle.scale.set(scale);
        sprites.body.scale.set(scale);
        setTint(sprites.handle, CREAM);
        setTint(sprites.body, clubColor(palette, color, index));
        sprites.glow.visible = prop?.boosted === true;
        if (sprites.glow.visible) {
          put(sprites.glow, reach, slot.across, 0);
          const beat = calm ? 0.5 : 0.35 + 0.35 * fade(input.sinceBeat, 3);
          sprites.glow.scale.set((BOOST_GLOW * scale * UNIT) / textures.halo.radius);
          sprites.glow.alpha = Math.min(1, light.haloAlpha * (1.2 + beat));
          setTint(sprites.glow, palette.or);
        }
      }
      hideClubs(count);
      return 0;
    },
  };
}

export const JONGLEUR_DRAWING: ClassDrawing = {
  classId: 'jongleur',
  paint(density): PaintedDrawing {
    const { shapes, source } = paintSheet(JONGLEUR_PIECES, density);
    return {
      extent: EXTENT,
      height: 1.2,
      centerAhead: MIDDLE,
      shadow: shapes.shadow,
      source,
      create: (bodies: Container, glows: Container, textures: Textures) =>
        createJugglerLook(bodies, glows, textures, shapes),
    };
  },
};
